import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IResourceComponentsProps, useShow, useTranslate, useNavigation } from "@refinedev/core";
import { Show, EditButton, ShowButton } from "@refinedev/mui";
import {
    Stack,
    Typography,
    Box,
    Grid,
    Card,
    CardContent,
    Avatar,
    useTheme,
    alpha,
    Chip,
    Button,
    IconButton,
    Tooltip,
    CircularProgress,
    ToggleButtonGroup,
    ToggleButton,
    Divider,
    useMediaQuery,
    Select,
    MenuItem,
    FormControl,
    SelectChangeEvent,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
    People,
    Edit,
    CheckCircle,
    Warning,
    Add,
    Delete,
    AccountTree,
    SubdirectoryArrowRight,
    ViewList,
    Park,
    PlaylistAddCheck,
    PersonAdd,
    SupervisorAccount,
    Cancel,
    Phone,
    Group,
    SwapHoriz,
} from "@mui/icons-material";
import { Building2, AlertCircle } from "lucide-react";
import { LoadingSkeleton } from "../../components/common/LoadingSkeleton";
import { SectionCard } from "../../components/common/SectionCard";
import { StatusBadge } from "../../components/common/StatusBadge";
import { useAuth } from "../../contexts/auth-context";
import { canModifyResource, UserRole } from "../../utils/role-permissions";
import { useSubGroups, SubGroup, CreateSubGroupDto, UpdateSubGroupDto, SubGroupTreeNode } from "../../hooks/useSubGroups";
import { SubGroupFormDialog, AvailableUser } from "../../components/sub-groups/SubGroupFormDialog";
import { SubGroupTreeView } from "../../components/sub-groups/SubGroupTreeView";
import axiosInstance from "../../api/axios-config";

// User interface for the users list
interface IUser {
    id: number;
    email: string;
    fullname: string;
    phone?: string;
    hasBeenOnboarded: boolean;
    profilePicture?: string;
    role?: string;
    subGroupId?: number;
    subGroup?: {
        id: number;
        name: string;
    };
}

export const CompanyShow: React.FC<IResourceComponentsProps> = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const navigate = useNavigate();
    const { queryResult } = useShow();
    const { data, isLoading } = queryResult;
    const { edit } = useNavigation();
    const { user: currentUser } = useAuth();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Get company ID from URL for useSubGroups hook
    const { id: companyIdFromUrl } = useParams<{ id: string }>();
    const companyId = companyIdFromUrl ? parseInt(companyIdFromUrl, 10) : undefined;

    // Sub-groups state - pass company ID for ADMIN/SAFEE_STAFF
    const {
        subGroups,
        loading: subGroupsLoading,
        createSubGroup,
        updateSubGroup,
        deleteSubGroup,
        moveSubGroup,
        fetchTree,
    } = useSubGroups(companyId);

    // Main view mode: 'subgroups' or 'users'
    const [mainViewMode, setMainViewMode] = useState<'subgroups' | 'users'>('users');

    // Sub-groups view mode: 'list' or 'tree'
    const [viewMode, setViewMode] = useState<'list' | 'tree'>('list');

    // Users list state
    const [enterpriseUsers, setEnterpriseUsers] = useState<IUser[]>([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [treeData, setTreeData] = useState<SubGroupTreeNode[]>([]);
    const [treeLoading, setTreeLoading] = useState(false);

    // Fetch tree data when switching to tree view
    useEffect(() => {
        if (viewMode === 'tree' && treeData.length === 0) {
            const loadTree = async () => {
                setTreeLoading(true);
                try {
                    const data = await fetchTree();
                    setTreeData(data);
                } catch (err) {
                    console.error('Failed to fetch tree:', err);
                } finally {
                    setTreeLoading(false);
                }
            };
            loadTree();
        }
    }, [viewMode, fetchTree, treeData.length]);

    // Refresh tree data when subGroups change
    useEffect(() => {
        if (viewMode === 'tree' && subGroups.length > 0) {
            const refreshTree = async () => {
                try {
                    const data = await fetchTree();
                    setTreeData(data);
                } catch (err) {
                    console.error('Failed to refresh tree:', err);
                }
            };
            refreshTree();
        }
    }, [subGroups, viewMode, fetchTree]);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingSubGroup, setEditingSubGroup] = useState<SubGroup | null>(null);
    const [dialogError, setDialogError] = useState<string | null>(null);
    const [dialogLoading, setDialogLoading] = useState(false);

    // Available users for assignment
    const [availableUsers, setAvailableUsers] = useState<AvailableUser[]>([]);
    const [loadingUsers, setLoadingUsers] = useState(false);

    // Track presence of moderators and users for onboarding card
    const [hasModerator, setHasModerator] = useState<boolean | null>(null);
    const [hasUsers, setHasUsers] = useState<boolean | null>(null);
    const [loadingRoleCounts, setLoadingRoleCounts] = useState(true);

    // Count of moderators and employees
    const [moderatorCount, setModeratorCount] = useState<number>(0);
    const [employeeCount, setEmployeeCount] = useState<number>(0);

    const record = data?.data;

    // Fetch available users for sub-group assignment
    useEffect(() => {
        const fetchUsers = async () => {
            setLoadingUsers(true);
            try {
                // Request all users since we need to filter by subGroupId locally
                const response = await axiosInstance.get('/users?limit=10000');
                // Handle both array response (legacy) and paginated response {data: [...], total: ...}
                let usersData: any[] = [];
                if (Array.isArray(response.data)) {
                    usersData = response.data;
                } else if (response.data && Array.isArray(response.data.data)) {
                    usersData = response.data.data;
                }
                // Filter only users without a sub-group assigned
                const usersWithoutSubGroup = usersData
                    .filter((u: any) => !u.subGroupId)
                    .map((u: any) => ({
                        id: u.id,
                        fullname: u.fullname,
                        email: u.email,
                        profilePicture: u.profilePicture,
                    }));
                setAvailableUsers(usersWithoutSubGroup);
            } catch (error) {
                console.error('Failed to fetch users:', error);
            } finally {
                setLoadingUsers(false);
            }
        };

        // Only fetch if dialog is about to open for creation
        if (currentUser?.role && ['ADMIN', 'MODERATOR', 'MODERATOR_FREE', 'SAFEE_MODERATOR'].includes(currentUser.role)) {
            fetchUsers();
        }
    }, [currentUser?.role]);

    // Fetch role counts to check for moderators and users presence
    useEffect(() => {
        const fetchRoleCounts = async () => {
            if (!record?.id) return;

            setLoadingRoleCounts(true);
            try {
                const response = await axiosInstance.get(`/users?entrepriseId=${record.id}&limit=10000`);
                let usersData: any[] = [];
                if (Array.isArray(response.data)) {
                    usersData = response.data;
                } else if (response.data && Array.isArray(response.data.data)) {
                    usersData = response.data.data;
                }

                // Filter to ensure only users from this company (safety check)
                const companyUsers = usersData.filter((u: any) =>
                    u.entrepriseId === record.id || u.entrepriseId === Number(record.id)
                );

                // Check for moderators (MODERATOR, MODERATOR_FREE, or SUB_MODERATOR)
                const moderators = companyUsers.filter((u: any) =>
                    u.role === 'MODERATOR' || u.role === 'MODERATOR_FREE' || u.role === 'SUB_MODERATOR'
                );
                setHasModerator(moderators.length > 0);
                setModeratorCount(moderators.length);

                // Check for regular users (USER role)
                const regularUsers = companyUsers.filter((u: any) => u.role === 'USER');
                setHasUsers(regularUsers.length > 0);
                setEmployeeCount(regularUsers.length);

                // Store users for the users view
                setEnterpriseUsers(companyUsers);
            } catch (error) {
                console.error('Failed to fetch role counts:', error);
                setHasModerator(null);
                setHasUsers(null);
                setModeratorCount(0);
                setEmployeeCount(0);
            } finally {
                setLoadingRoleCounts(false);
            }
        };

        fetchRoleCounts();
    }, [record?.id]);

    // Check if user can modify this company
    const canModify = currentUser?.role
        ? canModifyResource(currentUser.role as UserRole, 'company') &&
          (currentUser.role === 'ADMIN' ||
           currentUser.role === 'SAFEE_STAFF' ||
           (currentUser.role === 'MODERATOR' && currentUser.entrepriseId === record?.id))
        : false;

    // SAFEE_STAFF can modify but not delete sub-groups
    const canDeleteSubGroup = currentUser?.role
        ? ['ADMIN', 'MODERATOR', 'MODERATOR_FREE'].includes(currentUser.role) &&
          (currentUser.role === 'ADMIN' ||
           (currentUser.entrepriseId === record?.id))
        : false;

    const isGuest = currentUser?.role === 'GUEST';
    const isVigilane = currentUser?.role === 'VIGILANE';

    // State for tracking user assignment loading
    const [assigningUserId, setAssigningUserId] = useState<number | null>(null);

    // State for tracking role change loading
    const [changingRoleUserId, setChangingRoleUserId] = useState<number | null>(null);

    // Build hierarchical options for sub-group select
    const buildHierarchicalOptions = useCallback((groups: SubGroup[], parentId: number | null = null, depth = 0): Array<{ id: number; name: string; depth: number }> => {
        const result: Array<{ id: number; name: string; depth: number }> = [];
        const children = groups.filter(g => g.parentId === parentId);

        for (const child of children) {
            result.push({ id: child.id, name: child.name, depth });
            result.push(...buildHierarchicalOptions(groups, child.id, depth + 1));
        }

        return result;
    }, []);

    const hierarchicalSubGroupOptions = useMemo(() => {
        return buildHierarchicalOptions(subGroups);
    }, [subGroups, buildHierarchicalOptions]);

    // Handler for quick sub-group assignment
    const handleQuickAssignSubGroup = useCallback(async (userId: number, subGroupId: number | null) => {
        setAssigningUserId(userId);
        try {
            if (subGroupId === null) {
                // Find the current sub-group of the user to remove them
                const user = enterpriseUsers.find(u => u.id === userId);
                if (user?.subGroupId) {
                    await axiosInstance.delete(`/sub-groups/${user.subGroupId}/members/${userId}`);
                }
            } else {
                await axiosInstance.post(`/sub-groups/${subGroupId}/members/${userId}`);
            }
            // Refresh users list
            const response = await axiosInstance.get(`/users?entrepriseId=${record?.id}&limit=10000`);
            let usersData: any[] = [];
            if (Array.isArray(response.data)) {
                usersData = response.data;
            } else if (response.data && Array.isArray(response.data.data)) {
                usersData = response.data.data;
            }
            const companyUsers = usersData.filter((u: any) =>
                u.entrepriseId === record?.id || u.entrepriseId === Number(record?.id)
            );
            setEnterpriseUsers(companyUsers);
        } catch (err: any) {
            console.error('Failed to assign sub-group:', err);
            alert(err?.response?.data?.message || translate("errors.generic", "Une erreur est survenue"));
        } finally {
            setAssigningUserId(null);
        }
    }, [record?.id, enterpriseUsers, translate]);

    // Handler for changing user role
    const handleChangeUserRole = useCallback(async (userId: number, newRole: string) => {
        setChangingRoleUserId(userId);
        try {
            await axiosInstance.patch(`/users/${userId}`, { role: newRole });
            // Update user role in local state
            setEnterpriseUsers(prev =>
                prev.map(u => u.id === userId ? { ...u, role: newRole } : u)
            );
            // Update counts
            const updatedUsers = enterpriseUsers.map(u => u.id === userId ? { ...u, role: newRole } : u);
            const moderators = updatedUsers.filter((u: any) =>
                u.role === 'MODERATOR' || u.role === 'MODERATOR_FREE' || u.role === 'SUB_MODERATOR'
            );
            setModeratorCount(moderators.length);
            const regularUsers = updatedUsers.filter((u: any) => u.role === 'USER');
            setEmployeeCount(regularUsers.length);
        } catch (err: any) {
            console.error('Failed to change user role:', err);
            alert(err?.response?.data?.message || translate("errors.generic", "Une erreur est survenue"));
        } finally {
            setChangingRoleUserId(null);
        }
    }, [enterpriseUsers, translate]);

    // Get available roles for the dropdown based on current user's role
    const getAvailableRoles = useCallback(() => {
        // SUB_MODERATOR should not see MODERATOR option
        if (currentUser?.role === 'SUB_MODERATOR') {
            return [
                { value: 'USER', label: translate("users.roles.user", "Utilisateur") },
                { value: 'SUB_MODERATOR', label: translate("users.roles.sub_moderator", "Responsable") },
            ];
        }
        // Other roles can see all enterprise roles
        return [
            { value: 'USER', label: translate("users.roles.user", "Utilisateur") },
            { value: 'SUB_MODERATOR', label: translate("users.roles.sub_moderator", "Responsable") },
            { value: 'MODERATOR', label: translate("users.roles.moderator", "Modérateur") },
        ];
    }, [currentUser?.role, translate]);

    // User columns for the DataGrid
    const userColumns: GridColDef<IUser>[] = useMemo(() => [
        {
            field: "user",
            headerName: translate("users.fields.user", "Utilisateur"),
            flex: 1,
            minWidth: isMobile ? 200 : 300,
            renderCell: ({ row }) => (
                <Stack direction="row" spacing={isMobile ? 1 : 2} alignItems="center">
                    <Avatar
                        src={row.profilePicture}
                        sx={{
                            width: isMobile ? 32 : 40,
                            height: isMobile ? 32 : 40,
                            bgcolor: row.hasBeenOnboarded ? 'success.main' : 'grey.400'
                        }}
                    >
                        {row.fullname?.[0]?.toUpperCase()}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                            {row.fullname}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                            {row.email}
                        </Typography>
                    </Box>
                    {row.id === currentUser?.id && (
                        <Chip
                            label={translate("users.me", "Moi")}
                            size="small"
                            color="primary"
                            sx={{
                                fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                height: { xs: '20px', sm: '24px' },
                                fontWeight: 600
                            }}
                        />
                    )}
                </Stack>
            ),
        },
        {
            field: "role",
            headerName: translate("users.fields.role", "Rôle"),
            width: isMobile ? 130 : 160,
            renderCell: ({ row }) => {
                const isChangingRole = changingRoleUserId === row.id;
                const roleColors: Record<string, string> = {
                    'MODERATOR': theme.palette.success.main,
                    'MODERATOR_FREE': theme.palette.success.main,
                    'SUB_MODERATOR': theme.palette.warning.main,
                    'USER': theme.palette.text.secondary,
                };
                const roleLabels: Record<string, string> = {
                    'MODERATOR': translate("users.roles.moderator", "Modérateur"),
                    'MODERATOR_FREE': translate("users.roles.moderator_free", "Modérateur"),
                    'SUB_MODERATOR': translate("users.roles.sub_moderator", "Responsable"),
                    'USER': translate("users.roles.user", "Utilisateur"),
                };

                // Check if user can edit this role (cannot edit higher roles like ADMIN, SAFEE_MODERATOR)
                const isHigherRole = ['ADMIN', 'SAFEE_MODERATOR'].includes(row.role || '');
                // Cannot change own role
                const isSelf = row.id === currentUser?.id;
                // MODERATOR_FREE shown as read-only (it's a special billing-related role)
                const isModeratorFree = row.role === 'MODERATOR_FREE';

                // Show read-only chip for higher roles, self, or MODERATOR_FREE
                if (isHigherRole || isSelf || isModeratorFree || !canModify || isGuest || isVigilane) {
                    return (
                        <Chip
                            label={roleLabels[row.role || 'USER'] || row.role}
                            size="small"
                            sx={{
                                borderColor: alpha(roleColors[row.role || 'USER'] || theme.palette.divider, 0.5),
                                color: roleColors[row.role || 'USER'] || theme.palette.text.secondary,
                            }}
                            variant="outlined"
                        />
                    );
                }

                // Editable dropdown for other users
                return (
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                        <Select
                            value={row.role || 'USER'}
                            onChange={(e: SelectChangeEvent<string>) => {
                                handleChangeUserRole(row.id, e.target.value);
                            }}
                            disabled={isChangingRole}
                            variant="outlined"
                            sx={{
                                '& .MuiSelect-select': {
                                    py: 0.5,
                                    fontSize: '0.875rem',
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: alpha(roleColors[row.role || 'USER'] || theme.palette.divider, 0.5),
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: roleColors[row.role || 'USER'] || theme.palette.primary.main,
                                },
                            }}
                            renderValue={(value) => {
                                if (isChangingRole) {
                                    return <CircularProgress size={16} />;
                                }
                                return (
                                    <Typography
                                        variant="body2"
                                        sx={{ color: roleColors[value as string] || theme.palette.text.secondary }}
                                    >
                                        {roleLabels[value as string] || value}
                                    </Typography>
                                );
                            }}
                        >
                            {getAvailableRoles().map((role) => (
                                <MenuItem key={role.value} value={role.value}>
                                    <Typography variant="body2">{role.label}</Typography>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                );
            },
        },
        {
            field: "phone",
            headerName: translate("users.fields.phone", "Téléphone"),
            width: isMobile ? 120 : 150,
            renderCell: ({ value }) => (
                <Stack direction="row" spacing={1} alignItems="center">
                    <Phone fontSize="small" color="action" />
                    <Typography variant="body2">{value || '-'}</Typography>
                </Stack>
            ),
        },
        {
            field: "hasBeenOnboarded",
            headerName: translate("users.fields.status", "Statut"),
            width: isMobile ? 120 : 150,
            renderCell: ({ value }) => (
                <Chip
                    label={value ? translate("users.status.active", "Actif") : translate("users.status.pending", "En attente")}
                    color={value ? "success" : "warning"}
                    size="small"
                    icon={value ? <CheckCircle /> : <Cancel />}
                />
            ),
        },
        {
            field: "subGroup",
            headerName: translate("users.fields.subGroup", "Sous-groupe"),
            width: isMobile ? 160 : 200,
            renderCell: ({ row }) => {
                const isAssigning = assigningUserId === row.id;

                // If user has a sub-group, show a chip with option to change
                if (row.subGroup && canModify && !isGuest && !isVigilane) {
                    return (
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <Select
                                value={row.subGroupId || ''}
                                onChange={(e: SelectChangeEvent<number | string>) => {
                                    const value = e.target.value;
                                    handleQuickAssignSubGroup(row.id, value === '' ? null : Number(value));
                                }}
                                disabled={isAssigning}
                                displayEmpty
                                variant="outlined"
                                sx={{
                                    '& .MuiSelect-select': {
                                        py: 0.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: alpha(theme.palette.primary.main, 0.3),
                                    },
                                }}
                                renderValue={(value) => {
                                    if (isAssigning) {
                                        return <CircularProgress size={16} />;
                                    }
                                    const selectedGroup = subGroups.find(g => g.id === value);
                                    return (
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <AccountTree fontSize="small" sx={{ color: 'primary.main', fontSize: 16 }} />
                                            <Typography variant="body2" noWrap>
                                                {selectedGroup?.name || translate("users.subGroup.unassigned", "Non assigné")}
                                            </Typography>
                                        </Stack>
                                    );
                                }}
                            >
                                <MenuItem value="">
                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                        {translate("users.subGroup.unassigned", "Non assigné")}
                                    </Typography>
                                </MenuItem>
                                {hierarchicalSubGroupOptions.map((option) => (
                                    <MenuItem key={option.id} value={option.id}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            {option.depth > 0 && (
                                                <Box sx={{ width: option.depth * 16, display: 'flex', justifyContent: 'flex-end', mr: 0.5 }}>
                                                    <SubdirectoryArrowRight fontSize="small" sx={{ color: 'text.disabled', fontSize: 14 }} />
                                                </Box>
                                            )}
                                            <Typography variant="body2">{option.name}</Typography>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    );
                }

                // If user has sub-group but no edit permission, show read-only chip
                if (row.subGroup) {
                    return (
                        <Chip
                            label={row.subGroup.name}
                            size="small"
                            icon={<AccountTree fontSize="small" />}
                            sx={{ cursor: 'default' }}
                        />
                    );
                }

                // If no sub-group and has edit permission, show select dropdown
                if (canModify && !isGuest && !isVigilane && subGroups.length > 0) {
                    return (
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <Select
                                value=""
                                onChange={(e: SelectChangeEvent<number | string>) => {
                                    const value = e.target.value;
                                    if (value !== '') {
                                        handleQuickAssignSubGroup(row.id, Number(value));
                                    }
                                }}
                                disabled={isAssigning}
                                displayEmpty
                                variant="outlined"
                                sx={{
                                    '& .MuiSelect-select': {
                                        py: 0.5,
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: alpha(theme.palette.divider, 0.5),
                                        borderStyle: 'dashed',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: alpha(theme.palette.primary.main, 0.5),
                                    },
                                }}
                                renderValue={() => {
                                    if (isAssigning) {
                                        return <CircularProgress size={16} />;
                                    }
                                    return (
                                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                            {translate("users.subGroup.none", "Aucun")}
                                        </Typography>
                                    );
                                }}
                            >
                                {hierarchicalSubGroupOptions.map((option) => (
                                    <MenuItem key={option.id} value={option.id}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            {option.depth > 0 && (
                                                <Box sx={{ width: option.depth * 16, display: 'flex', justifyContent: 'flex-end', mr: 0.5 }}>
                                                    <SubdirectoryArrowRight fontSize="small" sx={{ color: 'text.disabled', fontSize: 14 }} />
                                                </Box>
                                            )}
                                            <Typography variant="body2">{option.name}</Typography>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    );
                }

                // No permission or no sub-groups, show text
                return (
                    <Typography variant="caption" color="text.secondary">
                        {translate("users.subGroup.unassigned", "Non assigné")}
                    </Typography>
                );
            },
        },
        {
            field: "actions",
            headerName: "",
            width: isMobile ? 90 : 120,
            sortable: false,
            renderCell: ({ row }) => (
                <Stack direction="row" spacing={isMobile ? 0.5 : 1}>
                    {canModify && !isVigilane && (
                        <Tooltip title={translate("buttons.edit", "Modifier")}>
                            <span>
                                <EditButton size={isMobile ? 'small' : 'medium'} hideText recordItemId={row.id} resource="users" />
                            </span>
                        </Tooltip>
                    )}
                    <Tooltip title={translate("buttons.show", "Voir")}>
                        <span>
                            <ShowButton size={isMobile ? 'small' : 'medium'} hideText recordItemId={row.id} resource="users" />
                        </span>
                    </Tooltip>
                </Stack>
            ),
        },
    ], [translate, isMobile, currentUser?.id, canModify, isVigilane, isGuest, subGroups, hierarchicalSubGroupOptions, assigningUserId, handleQuickAssignSubGroup, theme, changingRoleUserId, handleChangeUserRole, getAvailableRoles]);

    // Sub-groups handlers
    const handleOpenCreateDialog = useCallback(() => {
        setEditingSubGroup(null);
        setDialogError(null);
        setDialogOpen(true);
    }, []);

    const handleOpenEditDialog = useCallback((subGroup: SubGroup) => {
        setEditingSubGroup(subGroup);
        setDialogError(null);
        setDialogOpen(true);
    }, []);

    const handleCloseDialog = useCallback(() => {
        setDialogOpen(false);
        setEditingSubGroup(null);
        setDialogError(null);
    }, []);

    const handleSubmitSubGroup = useCallback(async (data: CreateSubGroupDto | UpdateSubGroupDto, selectedUserIds?: number[]) => {
        setDialogLoading(true);
        setDialogError(null);
        try {
            if (editingSubGroup) {
                await updateSubGroup(editingSubGroup.id, data as UpdateSubGroupDto);
            } else {
                // Create the sub-group first
                // Include entrepriseId for ADMIN/SAFEE_STAFF who may not have an associated company
                const createData: CreateSubGroupDto = {
                    ...(data as CreateSubGroupDto),
                    entrepriseId: record?.id ? Number(record.id) : undefined,
                };
                const newSubGroup = await createSubGroup(createData);

                // If users were selected, assign them to the new sub-group
                if (selectedUserIds && selectedUserIds.length > 0 && newSubGroup?.id) {
                    try {
                        await axiosInstance.post(`/sub-groups/${newSubGroup.id}/members`, {
                            userIds: selectedUserIds,
                        });
                    } catch (assignError: any) {
                        console.error('Failed to assign users:', assignError);
                        // Don't fail the whole operation, just log the error
                        // The sub-group was created successfully
                    }
                }
            }
            handleCloseDialog();
        } catch (err: any) {
            setDialogError(err?.response?.data?.message || translate("errors.generic", "Une erreur est survenue"));
        } finally {
            setDialogLoading(false);
        }
    }, [editingSubGroup, createSubGroup, updateSubGroup, handleCloseDialog, translate, record]);

    const handleDeleteSubGroup = useCallback(async (id: number) => {
        if (!window.confirm(translate("company.subGroups.confirmDelete", "\u00cates-vous s\u00fbr de vouloir supprimer ce sous-groupe ?"))) {
            return;
        }
        try {
            await deleteSubGroup(id);
        } catch (err: any) {
            alert(err?.response?.data?.message || translate("errors.generic", "Une erreur est survenue"));
        }
    }, [deleteSubGroup, translate]);

    const handleMoveSubGroup = useCallback(async (subGroupId: number, newParentId: number | null) => {
        try {
            await moveSubGroup(subGroupId, newParentId);
        } catch (err: any) {
            setDialogError(err?.response?.data?.message || translate("errors.generic", "Une erreur est survenue lors du déplacement"));
            throw err;
        }
    }, [moveSubGroup, translate]);

    // DataGrid columns for sub-groups
    const subGroupColumns: GridColDef[] = [
        {
            field: 'name',
            headerName: translate("company.subGroups.fields.name", "Nom"),
            flex: 1,
            minWidth: 200,
            renderCell: ({ row }) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: (row.depth || 0) * 24 }} />
                    {row.parent && <SubdirectoryArrowRight fontSize="small" color="action" />}
                    <Typography variant="body2">{row.name}</Typography>
                </Box>
            ),
        },
        {
            field: 'description',
            headerName: translate("company.subGroups.fields.description", "Description"),
            flex: 1.5,
            minWidth: 200,
            renderCell: ({ row }) => (
                <Typography variant="body2" color="text.secondary" noWrap>
                    {row.description || '-'}
                </Typography>
            ),
        },
        {
            field: 'usersCount',
            headerName: translate("company.subGroups.fields.members", "Membres"),
            width: 120,
            align: 'center',
            headerAlign: 'center',
            renderCell: ({ row }) => (
                <Chip
                    label={row._count?.users || 0}
                    size="small"
                    icon={<People sx={{ fontSize: 14, ml: 0 }} />}
                    sx={{ pl: 1 }}
                    variant="outlined"
                />
            ),
        },
        {
            field: 'childrenCount',
            headerName: translate("company.subGroups.fields.subGroups", "Sous-groupes"),
            width: 130,
            align: 'center',
            headerAlign: 'center',
            renderCell: ({ row }) => (
                <Chip
                    label={row._count?.children || 0}
                    size="small"
                    icon={<AccountTree sx={{ fontSize: 14, ml: 0 }} />}
                    sx={{ pl: 1 }}
                    variant="outlined"
                    color={row._count?.children > 0 ? "primary" : "default"}
                />
            ),
        },
        {
            field: 'actions',
            headerName: '',
            width: 140,
            sortable: false,
            renderCell: ({ row }) => (
                <Stack direction="row" spacing={0.5}>
                    <Tooltip title={translate("company.subGroups.manageMembers", "Gerer les membres")}>
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={() => navigate(`/company/sub-groups/${row.id}/members`)}
                        >
                            <People fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={translate("buttons.edit", "Modifier")}>
                        <IconButton size="small" onClick={() => handleOpenEditDialog(row)}>
                            <Edit fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    {canDeleteSubGroup && (
                        <Tooltip title={translate("buttons.delete", "Supprimer")}>
                            <IconButton size="small" color="error" onClick={() => handleDeleteSubGroup(row.id)}>
                                <Delete fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>
            ),
        },
    ];

    if (isLoading) {
        return <LoadingSkeleton />;
    }

    if (!record) {
        return (
            <Show
                headerButtons={({ defaultButtons }) => (
                    <>
                        {defaultButtons}
                    </>
                )}
            >
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                        {translate("company.notFound", "Organisation non trouvée")}
                    </Typography>
                </Box>
            </Show>
        );
    }

    const userPercentage = record.limitUser ? (record.count / record.limitUser) * 100 : 0;
    const isNearLimit = userPercentage >= 80;
    const isActive = record.count > 0;


    return (
        <Show
            headerButtons={false}
            footerButtons={false}
            headerProps={{
                sx: { display: 'none' }
            }}
            footerButtonProps={{
                sx: { display: 'none' }
            }}
            breadcrumb={false}
            goBack={false}
            title={false}
        >
            <Grid container spacing={3} sx={{ minHeight: 'calc(100vh - 200px)' }}>
                {/* Left Column - Sidebar */}
                <Grid item xs={12} md={3} sx={{ display: 'flex' }}>
                    <Card
                        sx={{
                            background: theme.palette.mode === 'dark'
                                ? `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.1)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`
                                : `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.05)} 0%, ${theme.palette.background.paper} 100%)`,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                            overflow: 'visible',
                            width: '100%',
                        }}
                    >
                        <CardContent sx={{ p: 3 }}>
                            {/* Logo */}
                            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                                <Avatar
                                    src={record.logo || undefined}
                                    sx={{
                                        width: 100,
                                        height: 100,
                                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                                        color: theme.palette.primary.main,
                                        boxShadow: theme.shadows[4],
                                        border: `4px solid ${theme.palette.background.paper}`,
                                    }}
                                >
                                    {!record.logo && <Building2 size={48} />}
                                </Avatar>
                            </Box>

                            {/* Company Name */}
                            <Typography variant="h5" sx={{ fontWeight: 600, textAlign: 'center', mb: 2 }}>
                                {record.nom}
                            </Typography>

                            {/* Edit Button - under company name */}
                            {canModify && !isGuest && (
                                <Box sx={{ mb: 3 }}>
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        startIcon={<Edit />}
                                        onClick={() => record.id && edit('company', record.id)}
                                        color="primary"
                                        disabled={!record.id}
                                    >
                                        {translate("buttons.edit", "Modifier")}
                                    </Button>
                                </Box>
                            )}

                            {/* Action Buttons */}
                            {canModify && !isGuest && (
                                <Stack spacing={1.5} sx={{ mb: 3 }}>
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        startIcon={<SupervisorAccount />}
                                        onClick={() => navigate(`/users/create?entrepriseId=${record?.id}&role=MODERATOR`)}
                                        sx={{
                                            justifyContent: 'flex-start',
                                            borderColor: alpha(theme.palette.warning.main, 0.5),
                                            color: theme.palette.warning.dark,
                                            '&:hover': {
                                                borderColor: theme.palette.warning.main,
                                                bgcolor: alpha(theme.palette.warning.main, 0.08),
                                            },
                                        }}
                                    >
                                        {translate("company.sidebar.addModerator", "Ajouter un modérateur")}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        startIcon={<PersonAdd />}
                                        onClick={() => navigate(`/users/create?entrepriseId=${record?.id}&role=USER`)}
                                        sx={{
                                            justifyContent: 'flex-start',
                                            borderColor: alpha(theme.palette.info.main, 0.5),
                                            color: theme.palette.info.dark,
                                            '&:hover': {
                                                borderColor: theme.palette.info.main,
                                                bgcolor: alpha(theme.palette.info.main, 0.08),
                                            },
                                        }}
                                    >
                                        {translate("company.sidebar.addUser", "Ajouter un utilisateur")}
                                    </Button>
                                    {subGroups.length > 0 && (
                                        <Button
                                            variant="outlined"
                                            fullWidth
                                            startIcon={<SwapHoriz />}
                                            onClick={() => navigate(`/company/sub-groups/${subGroups[0].id}/members`)}
                                            sx={{
                                                justifyContent: 'flex-start',
                                                borderColor: alpha(theme.palette.primary.main, 0.5),
                                                color: theme.palette.primary.dark,
                                                '&:hover': {
                                                    borderColor: theme.palette.primary.main,
                                                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                                                },
                                            }}
                                        >
                                            {translate("company.subGroups.manageAssignments", "Gérer les assignations")}
                                        </Button>
                                    )}
                                </Stack>
                            )}

                            <Divider sx={{ my: 2 }} />

                            {/* Stats */}
                            <Stack spacing={2}>
                                {/* Sub-groups Count */}
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        {translate("company.sidebar.subGroups", "Sous-groupes")}
                                    </Typography>
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                                        <AccountTree sx={{ color: theme.palette.primary.main }} />
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            {subGroups.length}
                                        </Typography>
                                    </Stack>
                                </Box>

                                {/* Moderators Count */}
                                <Box>
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                            {translate("company.sidebar.moderators", "Modérateurs")}
                                        </Typography>
                                        {moderatorCount === 0 && (
                                            <Tooltip title={translate("company.sidebar.noModeratorWarning", "Aucun modérateur assigné")}>
                                                <Warning sx={{ fontSize: 16, color: theme.palette.error.main }} />
                                            </Tooltip>
                                        )}
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                                        <SupervisorAccount sx={{ color: moderatorCount === 0 ? theme.palette.error.main : theme.palette.warning.main }} />
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 600,
                                                color: moderatorCount === 0 ? theme.palette.error.main : 'inherit',
                                            }}
                                        >
                                            {moderatorCount}
                                        </Typography>
                                    </Stack>
                                </Box>

                                {/* Users Count */}
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        {translate("company.sidebar.users", "Utilisateurs")}
                                    </Typography>
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                                        <Group sx={{ color: theme.palette.primary.main }} />
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            {record.count || 0}
                                            <Typography component="span" variant="body2" color="text.secondary">
                                                /{record.limitUser || '∞'}
                                            </Typography>
                                        </Typography>
                                    </Stack>
                                    {record.limitUser && (
                                        <Box
                                            sx={{
                                                width: '100%',
                                                height: 4,
                                                bgcolor: alpha(theme.palette.divider, 0.2),
                                                borderRadius: 2,
                                                overflow: 'hidden',
                                                mt: 1
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: `${Math.min(userPercentage, 100)}%`,
                                                    height: '100%',
                                                    bgcolor: userPercentage >= 90
                                                        ? theme.palette.error.main
                                                        : userPercentage >= 80
                                                        ? theme.palette.warning.main
                                                        : theme.palette.primary.main,
                                                    transition: 'width 0.3s ease, background-color 0.3s ease',
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Right Column - Main Content */}
                <Grid item xs={12} md={9} sx={{ display: 'flex' }}>
                    <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CardContent sx={{ p: 0 }}>
                            {/* View Toggle Header */}
                            <Box
                                sx={{
                                    px: 3,
                                    py: 2,
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    gap: 2,
                                }}
                            >
                                <ToggleButtonGroup
                                    value={mainViewMode}
                                    exclusive
                                    onChange={(_, value) => value && setMainViewMode(value)}
                                    size="small"
                                >
                                    <ToggleButton value="users" sx={{ px: 3 }}>
                                        <People sx={{ mr: 1 }} fontSize="small" />
                                        {translate("company.tabs.users", "Utilisateurs")}
                                    </ToggleButton>
                                    <ToggleButton value="subgroups" sx={{ px: 3 }}>
                                        <AccountTree sx={{ mr: 1 }} fontSize="small" />
                                        {translate("company.tabs.subgroups", "Sous-groupes")}
                                    </ToggleButton>
                                </ToggleButtonGroup>

                                {/* Sub-view controls */}
                                {mainViewMode === 'subgroups' && canModify && !isGuest && (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <ToggleButtonGroup
                                            value={viewMode}
                                            exclusive
                                            onChange={(_, value) => value && setViewMode(value)}
                                            size="small"
                                        >
                                            <ToggleButton value="list">
                                                <Tooltip title={translate("company.subGroups.viewList", "Vue liste")}>
                                                    <ViewList fontSize="small" />
                                                </Tooltip>
                                            </ToggleButton>
                                            <ToggleButton value="tree">
                                                <Tooltip title={translate("company.subGroups.viewTree", "Vue arborescente")}>
                                                    <Park fontSize="small" />
                                                </Tooltip>
                                            </ToggleButton>
                                        </ToggleButtonGroup>
                                        {subGroups.length > 0 && (
                                            <Tooltip title={translate("company.subGroups.manageAssignments", "Gérer les assignations")}>
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<SwapHoriz />}
                                                    onClick={() => navigate(`/company/sub-groups/${subGroups[0].id}/members`)}
                                                    size="small"
                                                    sx={{
                                                        borderColor: alpha(theme.palette.primary.main, 0.5),
                                                        '&:hover': {
                                                            borderColor: theme.palette.primary.main,
                                                            bgcolor: alpha(theme.palette.primary.main, 0.04),
                                                        },
                                                    }}
                                                >
                                                    {translate("company.subGroups.manageAssignments", "Assignations")}
                                                </Button>
                                            </Tooltip>
                                        )}
                                        <Button
                                            variant="contained"
                                            startIcon={<Add />}
                                            onClick={handleOpenCreateDialog}
                                            size="small"
                                        >
                                            {translate("company.subGroups.create", "Créer")}
                                        </Button>
                                    </Stack>
                                )}

                                {mainViewMode === 'users' && canModify && !isGuest && (
                                    <Stack direction="row" spacing={1}>
                                        {subGroups.length > 0 && (
                                            <Button
                                                variant="outlined"
                                                startIcon={<SwapHoriz />}
                                                onClick={() => navigate(`/company/sub-groups/${subGroups[0].id}/members`)}
                                                size="small"
                                                sx={{
                                                    borderColor: alpha(theme.palette.primary.main, 0.5),
                                                    '&:hover': {
                                                        borderColor: theme.palette.primary.main,
                                                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                                                    },
                                                }}
                                            >
                                                {translate("company.subGroups.manageAssignments", "Gérer les assignations")}
                                            </Button>
                                        )}
                                        <Button
                                            variant="contained"
                                            startIcon={<PersonAdd />}
                                            onClick={() => navigate(`/users/create?entrepriseId=${record?.id}`)}
                                            size="small"
                                        >
                                            {translate("users.actions.add", "Ajouter un utilisateur")}
                                        </Button>
                                    </Stack>
                                )}
                            </Box>

                            {/* Content Area */}
                            <Box sx={{ p: 0 }}>
                                {mainViewMode === 'subgroups' ? (
                                    /* Sub-Groups View */
                                    <Box sx={{ width: '100%' }}>
                                        {subGroupsLoading || treeLoading ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                                <CircularProgress />
                                            </Box>
                                        ) : subGroups.length === 0 ? (
                                            <Box sx={{
                                                p: 4,
                                                textAlign: 'center',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                minHeight: 300,
                                            }}>
                                                <AccountTree sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                                                <Typography color="text.secondary">
                                                    {translate("company.subGroups.empty", "Aucun sous-groupe créé")}
                                                </Typography>
                                                <Typography variant="body2" color="text.disabled" sx={{ mt: 1, mb: 3 }}>
                                                    {translate("company.subGroups.emptyHint", "Créez des sous-groupes pour organiser vos équipes")}
                                                </Typography>
                                                {canModify && !isGuest && (
                                                    <Button
                                                        variant="contained"
                                                        startIcon={<Add />}
                                                        onClick={handleOpenCreateDialog}
                                                    >
                                                        {translate("company.subGroups.create", "Créer un sous-groupe")}
                                                    </Button>
                                                )}
                                            </Box>
                                        ) : viewMode === 'tree' ? (
                                            <SubGroupTreeView
                                                tree={treeData}
                                                loading={treeLoading}
                                                onNodeClick={(node) => {
                                                    const sg = subGroups.find(sg => sg.id === node.id);
                                                    if (sg) handleOpenEditDialog(sg);
                                                }}
                                            />
                                        ) : (
                                            <DataGrid
                                                rows={subGroups}
                                                columns={subGroupColumns}
                                                autoHeight
                                                disableRowSelectionOnClick
                                                pageSizeOptions={[5, 10, 25]}
                                                initialState={{
                                                    pagination: { paginationModel: { pageSize: 10 } },
                                                }}
                                                sx={{
                                                    border: 'none',
                                                    '& .MuiDataGrid-cell': {
                                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                                    },
                                                    '& .MuiDataGrid-columnHeaders': {
                                                        bgcolor: alpha(theme.palette.primary.main, 0.02),
                                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                                    },
                                                }}
                                            />
                                        )}
                                    </Box>
                                ) : (
                                    /* Users View */
                                    <Box sx={{ width: '100%' }}>
                                        {loadingRoleCounts || usersLoading ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                                <CircularProgress />
                                            </Box>
                                        ) : enterpriseUsers.length === 0 ? (
                                            <Box sx={{
                                                p: 4,
                                                textAlign: 'center',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                minHeight: 300,
                                            }}>
                                                <People sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                                                <Typography color="text.secondary">
                                                    {translate("company.users.empty", "Aucun utilisateur dans cette entreprise")}
                                                </Typography>
                                                <Typography variant="body2" color="text.disabled" sx={{ mt: 1, mb: 3 }}>
                                                    {translate("company.users.emptyHint", "Ajoutez des utilisateurs pour commencer")}
                                                </Typography>
                                                {canModify && !isGuest && (
                                                    <Button
                                                        variant="contained"
                                                        startIcon={<PersonAdd />}
                                                        onClick={() => navigate(`/users/create?entrepriseId=${record?.id}`)}
                                                    >
                                                        {translate("users.actions.add", "Ajouter un utilisateur")}
                                                    </Button>
                                                )}
                                            </Box>
                                        ) : (
                                            <DataGrid
                                                rows={enterpriseUsers}
                                                columns={userColumns}
                                                autoHeight
                                                disableRowSelectionOnClick
                                                pageSizeOptions={[5, 10, 25, 50]}
                                                initialState={{
                                                    pagination: { paginationModel: { pageSize: 10 } },
                                                }}
                                                columnVisibilityModel={{
                                                    phone: !isMobile,
                                                    hasBeenOnboarded: !isMobile,
                                                    subGroup: !isMobile && subGroups.length > 0,
                                                }}
                                                sx={{
                                                    border: 'none',
                                                    '& .MuiDataGrid-cell': {
                                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                                    },
                                                    '& .MuiDataGrid-columnHeaders': {
                                                        bgcolor: alpha(theme.palette.primary.main, 0.02),
                                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                                    },
                                                    '& .MuiDataGrid-row': {
                                                        '&:hover': {
                                                            bgcolor: alpha(theme.palette.primary.main, 0.04),
                                                        },
                                                    },
                                                }}
                                            />
                                        )}
                                    </Box>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Sub-Group Form Dialog */}
            <SubGroupFormDialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                onSubmit={handleSubmitSubGroup}
                onMove={handleMoveSubGroup}
                subGroup={editingSubGroup}
                parentOptions={subGroups}
                availableUsers={availableUsers}
                loadingUsers={loadingUsers}
                loading={dialogLoading}
                error={dialogError}
            />
        </Show >
    );
};
